import { createColumnHelper } from "@tanstack/react-table";
import _, { groupBy } from "lodash";
import { useMemo } from "react";
import { Alert } from "react-bootstrap";
import type { AccountHoldingWithSecurity } from "../../../../api/src/positions/positions.service";
import type { Transaction } from "../../../../api/src/transactions/lib";
import SecurityLink from "../../components/SecurityLink";
import { Table, useTable } from "../../components/Table/Table";
import { capitalize } from "../../lib/display";
import { formatCurrencyComponent, formatPercent } from "../../lib/numbers";
import { CASH_SYMBOLS } from "../../lib/security";

type AccountHoldingTotal = {
  value: number;
  realizedGainLoss: number;
  unrealizedGainLoss: number;
  units: number;
  appraisedUnitPrice: number;
  weight: number;
  modelWeight: number;
  modelWeightDifference: number;
  income: number;
};

const totalKeys: (keyof AccountHoldingTotal)[] = [
  "units",
  "appraisedUnitPrice",
  "value",
  "unrealizedGainLoss",
  "realizedGainLoss",
  "weight",
  "modelWeight",
  "modelWeightDifference",
  "income",
];
export type HoldingsFieldMode =
  | "units"
  | "appraisedUnitPrice"
  | "value"
  | "unrealizedGainLoss"
  | "realizedGainLoss"
  | "weight"
  | "modelWeight"
  | "modelWeightDifference"
  | "income";

const holdingsFieldModeMap: Record<HoldingsFieldMode, string> = {
  units: "Amount",
  appraisedUnitPrice: "Unit Price",
  value: "Value",
  unrealizedGainLoss: "Unrealized Gain/Loss",
  realizedGainLoss: "Realized Gain/Loss",
  weight: "Weight",
  modelWeight: "Model Weight",
  modelWeightDifference: "Model Weight Difference",
  income: "Income",
};

type AccountHoldingRow = {
  securityId: number;
  riskAllocation: string;
  categoryOrder: number;
  security: AccountHoldingWithSecurity["security"];
  totals: AccountHoldingTotal;
  accounts: Record<number, AccountHoldingTotal>;
};

// type AccountHoldingRow = Pick<
//   AccountHoldingWithSecurity,
//   | "id"
//   | "appraisedUnitPrice"
//   | "units"
//   | "value"
//   | "security"
//   | "unrealizedGainLoss"
//   | "accountId"
// >;

function getTableValue(
  fieldMode: HoldingsFieldMode,
  value: number,
  costBasis?: number,
) {
  if (value === 0) {
    return "–";
  }

  if (fieldMode === "unrealizedGainLoss" && typeof costBasis === "number") {
    const percentage = costBasis > 0 ? value / costBasis : 0;
    return (
      <div className="d-flex flex-column align-items-end">
        <div>{formatCurrencyComponent(value, 2)}</div>
        <div
          className="small text-muted"
          style={{ fontSize: "0.8rem", fontStyle: "italic" }}
        >
          {formatPercent(percentage, 2)}
        </div>
      </div>
    );
  }

  if (
    fieldMode === "weight" ||
    fieldMode === "modelWeight" ||
    fieldMode === "modelWeightDifference"
  ) {
    return formatPercent(value, 2);
  }

  if (fieldMode === "units") {
    return value.toLocaleString();
  }

  return formatCurrencyComponent(value, 2);
}

const AccountHoldingsTable = ({
  holdings,
  transactions,
  modelSummaries,
  riskCategories,
}: {
  holdings: AccountHoldingWithSecurity[];
  transactions: Transaction[];
  modelSummaries: {
    accountId: number;
    model: Record<string, number>;
  }[];
  riskCategories: string[];
}) => {
  const columnHelper = useMemo(
    () => createColumnHelper<AccountHoldingRow>(),
    [],
  );

  const flatColumns = useMemo(
    () => [
      // Hidden column for sorting by category order
      columnHelper.accessor((row) => row.categoryOrder, {
        id: "categoryOrder",
        header: () => null,
        cell: () => null,
        enableColumnFilter: false,
        meta: {
          className: "d-none",
          headerClassName: "d-none",
        },
      }),
      columnHelper.accessor((row) => row.security?.identifier ?? "", {
        id: "symbol",
        header: () => "Symbol",
        cell: (info) => (
          <SecurityLink
            symbol={info.getValue()}
            description={info.row.original.security?.description}
          />
        ),
        minSize: 260,
        enableColumnFilter: false,
      }),
      ...totalKeys.map((key) =>
        columnHelper.accessor((row) => row.totals[key], {
          id: key,
          cell: (info) => {
            const value = info.getValue();
            const costBasis =
              key === "unrealizedGainLoss"
                ? info.row.original.totals.value - value
                : undefined;
            return getTableValue(key, value, costBasis);
          },
          header: () => holdingsFieldModeMap[key],
          aggregatedCell: (info) => {
            const value = info.getValue();
            const costBasis =
              key === "unrealizedGainLoss"
                ? info.row.original.totals.value - value
                : undefined;
            const result = getTableValue(key, value, costBasis);

            if (key === "unrealizedGainLoss" && typeof result === "object") {
              return (
                <div className="d-flex flex-column align-items-end">
                  <div className="fw-bold">
                    {formatCurrencyComponent(value, 2)}
                  </div>
                  <div
                    className="small text-muted fw-bold"
                    style={{ fontSize: "0.8rem", fontStyle: "italic" }}
                  >
                    {formatPercent(
                      costBasis && costBasis > 0 ? value / costBasis : 0,
                      2,
                    )}
                  </div>
                </div>
              );
            }

            return <span className="fw-bold">{result}</span>;
          },
          minSize: 180,
          size: 180,
          enableColumnFilter: false,
          meta: {
            className: "text-end",
            headerClassName: "text-end",
          },
        }),
      ),
    ],
    [columnHelper],
  );

  function createRowsFromHoldings(
    holdings: AccountHoldingWithSecurity[],
    transactions: Transaction[],
    modelSummaries: {
      accountId: number;
      model: Record<string, number>;
    }[],
    riskCats: string[],
  ) {
    const cashHoldings = holdings.filter((holding) =>
      CASH_SYMBOLS.includes(holding.security?.symbol ?? ""),
    );
    const nonCashHoldings = holdings.filter(
      (holding) => !CASH_SYMBOLS.includes(holding.security?.symbol ?? ""),
    );

    // Build dynamic category map from firm risk categories
    const allHoldings: Record<string, AccountHoldingWithSecurity[]> = {};
    for (const category of riskCats) {
      allHoldings[category] = nonCashHoldings.filter(
        (h) => (h.security?.riskAllocation?.[category] ?? 0) > 0,
      );
    }
    // Always add cash at end
    allHoldings["Cash"] = cashHoldings;

    const transactionsBySecurity = groupBy(transactions, "securityId");
    const modelSummariesMap = _.keyBy(modelSummaries, "accountId");

    const totalValue = holdings.reduce(
      (sum, holding) => sum + holding.value,
      0,
    );

    // Use explicit category order: firm risk categories followed by Cash
    const orderedCategories = [...riskCats, "Cash"];

    return orderedCategories.flatMap(
      (riskAllocationCategory, categoryIndex) => {
        const riskAllocationHoldings =
          allHoldings[riskAllocationCategory] ?? [];
        const holdingsBySecurity = groupBy(
          riskAllocationHoldings,
          "securityId",
        );

        return Object.values(holdingsBySecurity).map(
          (securityHoldings): AccountHoldingRow => {
            const security = securityHoldings[0].security;
            const riskAllocationRatio =
              riskAllocationCategory === "Cash"
                ? 1
                : (security?.riskAllocation?.[riskAllocationCategory] ?? 0);
            const securityTransactions =
              transactionsBySecurity[securityHoldings[0].securityId] ?? [];

            const holdingsByAccount = groupBy(securityHoldings, "accountId");
            const transactionsByAccount = groupBy(
              securityTransactions,
              "accountId",
            );

            const holdingsByAccountTotals = Object.entries(
              holdingsByAccount,
            ).reduce<Record<number, AccountHoldingTotal>>(
              (result, [accountId, holdings]) => ({
                ...result,
                [parseInt(accountId)]: holdings.reduce(
                  (sum, holding): AccountHoldingTotal => {
                    const weight =
                      sum.weight +
                      (totalValue === 0 ? 0 : holding.value / totalValue) *
                        riskAllocationRatio;
                    const modelWeight =
                      sum.modelWeight +
                      (typeof security?.symbol === "undefined"
                        ? 0
                        : (modelSummariesMap[accountId]?.model[
                            security.symbol
                          ] ?? 0)) *
                        riskAllocationRatio;

                    return {
                      units: sum.units + holding.units * riskAllocationRatio,
                      appraisedUnitPrice:
                        (sum.appraisedUnitPrice + holding.appraisedUnitPrice) *
                        riskAllocationRatio,
                      value: sum.value + holding.value * riskAllocationRatio,
                      unrealizedGainLoss:
                        sum.unrealizedGainLoss +
                        holding.unrealizedGainLoss * riskAllocationRatio,
                      // A current holding has no `realizedGainLoss`
                      // (`AccountHoldingWithSecurity`,
                      // `api/src/positions/positions.service.ts`; APP-1653):
                      // no response ever carried one, so this total is 0 and
                      // the column shows "–", as it always has.
                      realizedGainLoss: sum.realizedGainLoss,
                      weight,
                      modelWeight: modelWeight,
                      modelWeightDifference: weight - modelWeight,
                      income:
                        sum.income +
                        (transactionsByAccount[accountId] ?? []).reduce(
                          (sum, transaction) => sum + (transaction.amount ?? 0),
                          0,
                        ) *
                          riskAllocationRatio,
                    };
                  },
                  {
                    units: 0,
                    appraisedUnitPrice: 0,
                    value: 0,
                    unrealizedGainLoss: 0,
                    realizedGainLoss: 0,
                    weight: 0,
                    modelWeight: 0,
                    modelWeightDifference: 0,
                    income: 0,
                  },
                ),
              }),
              {},
            );

            return {
              securityId: securityHoldings[0].securityId,
              riskAllocation: riskAllocationCategory,
              categoryOrder: categoryIndex,
              security,
              totals: Object.values(
                holdingsByAccountTotals,
              ).reduce<AccountHoldingTotal>(
                (sum, accountTotals) => {
                  const weight =
                    sum.weight +
                    (totalValue === 0 ? 0 : accountTotals.value / totalValue);
                  const modelWeight =
                    sum.modelWeight + accountTotals.modelWeight;

                  return {
                    units: sum.units + accountTotals.units,
                    appraisedUnitPrice:
                      sum.appraisedUnitPrice + accountTotals.appraisedUnitPrice,
                    value: sum.value + accountTotals.value,
                    unrealizedGainLoss:
                      sum.unrealizedGainLoss + accountTotals.unrealizedGainLoss,
                    realizedGainLoss:
                      sum.realizedGainLoss + accountTotals.realizedGainLoss,
                    weight,
                    modelWeight,
                    modelWeightDifference: weight - modelWeight,
                    income: sum.income + accountTotals.income,
                  };
                },
                {
                  units: 0,
                  appraisedUnitPrice: 0,
                  value: 0,
                  unrealizedGainLoss: 0,
                  realizedGainLoss: 0,
                  weight: 0,
                  modelWeight: 0,
                  modelWeightDifference: 0,
                  income: 0,
                },
              ),
              accounts: holdingsByAccountTotals,
            };
          },
        );
      },
    );
  }

  const accountsWithHoldings = useMemo(
    () =>
      createRowsFromHoldings(
        holdings,
        transactions,
        modelSummaries,
        riskCategories,
      ),
    [holdings, modelSummaries, transactions, riskCategories],
  );

  const groupedColumns = useMemo(
    () => [
      columnHelper.group({
        id: "category",
        header: "Category",
        columns: [
          columnHelper.accessor((row) => row.riskAllocation, {
            id: "riskAllocation",
            header: "Risk Category",
            cell: (info) => (
              <span className="fw-bold">{capitalize(info.getValue())}</span>
            ),
            minSize: 180,
            size: 180,
            enableColumnFilter: false,
            meta: {
              className: "position-sticky border-end",
              headerClassName: "position-sticky z-1",
              headerStyle: { left: 0 },
              style: { left: 0 },
            },
          }),
        ],
      }),
      ...flatColumns,
    ],
    [columnHelper, flatColumns],
  );
  const { table } = useTable({
    columns: groupedColumns,
    data: accountsWithHoldings,
    initialState: {
      sorting: [
        { id: "categoryOrder", desc: false },
        { id: "value", desc: true },
      ],
      grouping: ["riskAllocation"],
      pagination: {
        pageSize: 9999,
      },
    },
    manualPagination: true,
    getRowId: (row) => `${row.riskAllocation}-${row.securityId}`,
  });

  return (
    <>
      <Table table={table} />
      {holdings.length > 0 ? null : <Alert>No holdings found</Alert>}
    </>
  );
};

export default AccountHoldingsTable;
